import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatCardList,
} from '@evogard/ui';
import {
  FINANCE_DASHBOARD_BASIS_LABELS,
  FINANCE_DASHBOARD_DATE_KINDS,
  count,
  format as formatMoney,
  loadFailureMessage,
  subtract,
  sum,
  type FinanceDashboardBasis,
  type Money,
} from '@evogard/domain';
import type { FinanceTeamMapNodeWire } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import { useFullscreen } from '../../../lib/fullscreen.js';
import { useStoredFlag } from '../../../lib/stored-flag.js';
import {
  TeamMap,
  TeamMapLegendDot,
  TeamMapViewToggle,
  findNode,
  leavesOf,
  type TeamMapColumn,
  type TeamMapDot,
} from '../../../components/team-map/TeamMap.js';
import {
  PeriodFields,
  initialPeriod,
  periodRange,
} from '../../../components/team-map/period.js';
import { compact, money } from '../dashboard/money.js';

/** O valor curto sem o "R$" — no cartão do organograma, ao lado da bolinha da série, é o que cabe numa linha. */
const short = (v: number) => compact(v as Money).replace('R$ ', '');

type Node = FinanceTeamMapNodeWire;

const revenueOf = (n: Node) => money(n.values.receita.amount);
const expenseOf = (n: Node) =>
  sum(
    money(n.values.despesa_assistencia.amount),
    money(n.values.despesa_periferico.amount),
    money(n.values.despesa_sinistro.amount),
  );
const balanceOf = (n: Node) => subtract(revenueOf(n), expenseOf(n));
const entriesOf = (n: Node) =>
  n.values.receita.count +
  n.values.despesa_assistencia.count +
  n.values.despesa_periferico.count +
  n.values.despesa_sinistro.count;

const COLUMNS: TeamMapColumn<Node>[] = [
  {
    key: 'receita',
    label: 'Receita',
    dot: 'fase-verde',
    value: revenueOf,
    format: short,
    bar: true,
    inCard: true,
  },
  {
    key: 'assistencia',
    label: '24h',
    dot: 'fase-azul',
    value: (n) => money(n.values.despesa_assistencia.amount),
    format: short,
    bar: true,
  },
  {
    key: 'periferico',
    label: 'Periférico',
    dot: 'fase-turquesa',
    value: (n) => money(n.values.despesa_periferico.amount),
    format: short,
    bar: true,
  },
  {
    key: 'sinistro',
    label: 'Sinistro',
    dot: 'fase-vermelho',
    value: (n) => money(n.values.despesa_sinistro.amount),
    format: short,
    bar: true,
  },
  {
    key: 'despesa',
    label: 'Despesa',
    dot: 'fase-laranja',
    value: expenseOf,
    format: short,
    inCard: true,
  },
];

const badge = (n: Node) => {
  const s = balanceOf(n);
  if (s === 0 && revenueOf(n) === 0) return null;
  return { tone: s >= 0 ? ('sucesso' as const) : ('erro' as const), text: compact(s) };
};

/**
 * O mapa do time no Financeiro (29/10/2026): a receita e a despesa do
 * período, pela montagem de hoje do comercial — gestor, time, líder e
 * consultor, em organograma ou em tabela.
 *
 * As regras de quem leva cada lançamento estão em
 * `packages/domain/src/team-map.ts`, e as de quais lançamentos entram em
 * `apps/api/src/modules/finance/finance.team-map.service.ts` — os mesmos do
 * painel, então o total do mapa bate com o cartão do painel no mesmo período.
 */
export function FinanceTeamMapScreen() {
  const [period, setPeriod] = useState(initialPeriod);
  const [basis, setBasis] = useState<FinanceDashboardBasis>('competencia');
  const [view, setView] = useState<'organograma' | 'tabela'>('organograma');
  const [selected, setSelected] = useState<string | null>(null);
  /* A largura é preferência de quem olha: fica no navegador. A tela cheia não. */
  const [wide, setWide] = useStoredFlag('financeiro.mapa-do-time.largura-total', false);
  const fullscreen = useFullscreen<HTMLDivElement>();

  const range = periodRange(period);
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const query = { from: range.from, to: range.to, basis };
  const map = useQuery({
    queryKey: ['finance', 'team-map', tenantId, query],
    queryFn: async () =>
      ok(
        await api.financeTeamMap.teamMap({ query }),
        200,
        loadFailureMessage(undefined, 'o mapa do time'),
      ),
    enabled: tenantId !== undefined && !range.incomplete && !range.inverted,
    placeholderData: (previous) => previous,
  });
  const nodes = useMemo(() => map.data?.nodes ?? [], [map.data]);
  const root = nodes.find((n) => n.parentKey === null);
  const rank = useCallback((n: Node) => expenseOf(n), []);

  return (
    <Page measure="cheia">
      <PageHeader title="Mapa do time" />

      <div className="flex flex-wrap items-end gap-3">
        <PeriodFields value={period} onChange={setPeriod} />
        <div className="w-52">
          <Select
            label="Data do lançamento"
            value={basis}
            onChange={(e) => setBasis(e.target.value as FinanceDashboardBasis)}
          >
            {FINANCE_DASHBOARD_DATE_KINDS.map((b) => (
              <option key={b} value={b}>
                {FINANCE_DASHBOARD_BASIS_LABELS[b]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {range.inverted && (
        <Alert tone="erro">O início do período vem depois do fim.</Alert>
      )}
      {map.error && <Alert tone="erro">{map.error.message}</Alert>}

      <StatCardList stretch>
        <StatCard
          icon="seta-baixo"
          label="Receita"
          legend="fase-verde"
          value={root ? formatMoney(revenueOf(root)) : undefined}
          support={
            root
              ? count(root.values.receita.count, 'lançamento', 'lançamentos')
              : undefined
          }
        />
        <StatCard
          icon="seta-cima"
          label="Despesa"
          legend="fase-laranja"
          value={root ? formatMoney(expenseOf(root)) : undefined}
          support="24h, periférico e sinistro"
        />
        <StatCard
          icon="dinheiro"
          label="Saldo"
          value={root ? formatMoney(balanceOf(root)) : undefined}
          support="receita menos despesa"
        />
      </StatCardList>

      {/*
       * A área que cresce, como no Mapa do time do Sinistro. "Largura total" tira
       * o detalhe do lado e o põe embaixo, e o organograma fica mais alto; "Tela
       * cheia" leva o cartão e o detalhe para a tela inteira, com o detalhe de
       * volta ao lado.
       */}
      <div
        ref={fullscreen.ref}
        className={cn(
          fullscreen.active && 'overflow-auto bg-fundo p-4',
          fullscreen.pseudo && 'fixed inset-0 z-50',
        )}
      >
        <div
          className={cn(
            'grid items-start gap-4',
            (fullscreen.active || !wide) && 'lg:grid-cols-[minmax(0,1fr)_20rem]',
          )}
        >
          <Card
            bleed
            title={view === 'organograma' ? 'Organograma do time' : 'Tabela do time'}
            descricao="A montagem de hoje. Clique num cartão para ver o detalhe."
            actions={
              <div className="flex flex-wrap items-center justify-end gap-1">
                {!fullscreen.active && (
                  <Button
                    variant="discreto"
                    size="sm"
                    icon="mover"
                    className="max-lg:hidden"
                    aria-pressed={wide}
                    onClick={() => setWide(!wide)}
                  >
                    {wide ? 'Detalhe ao lado' : 'Largura total'}
                  </Button>
                )}
                <Button
                  variant={fullscreen.active ? 'secundario' : 'discreto'}
                  size="sm"
                  icon={fullscreen.active ? 'fechar' : 'expandir'}
                  aria-pressed={fullscreen.active}
                  title={fullscreen.active ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
                  onClick={fullscreen.toggle}
                >
                  {fullscreen.active ? 'Sair da tela cheia' : 'Tela cheia'}
                </Button>
                <TeamMapViewToggle value={view} onChange={setView} />
              </div>
            }
          >
            {map.isLoading ? (
              <p className="p-4 text-sm text-texto-suave">Carregando o mapa…</p>
            ) : nodes.length > 0 ? (
              <TeamMap
                view={view}
                nodes={nodes}
                columns={COLUMNS}
                badge={badge}
                rank={rank}
                footer={(n) => count(entriesOf(n), 'lançamento', 'lançamentos')}
                selected={selected}
                onSelect={setSelected}
                height={
                  fullscreen.active ? 'calc(100dvh - 7rem)' : wide ? '46rem' : undefined
                }
              />
            ) : null}
          </Card>
          <Detail nodes={nodes} selected={selected} onSelect={setSelected} />
        </div>
      </div>

      <p className="text-xs text-texto-suave">
        O número vai para o consultor que está no veículo hoje, e a árvore é a de hoje.
        Entram as receitas e as despesas lançadas no Financeiro, como no painel; a
        mensalidade (boleto) e a premiação ainda não. A receita avulsa é do associado, e o
        fechamento de assistência com mais de um carro não é dividido: os dois ficam em
        Sem consultor.
      </p>
    </Page>
  );
}

function Detail({
  nodes,
  selected,
  onSelect,
}: {
  nodes: readonly Node[];
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  const { node, parent } = findNode(nodes, selected);
  if (!node) return null;
  const leaves = leavesOf(nodes, node.key)
    .filter((n) => n.kind === 'consultor')
    .sort((a, b) => balanceOf(a) - balanceOf(b))
    .slice(0, 5);
  const rows: [string, TeamMapDot, Money, number][] = [
    ['Receita', 'fase-verde', revenueOf(node), node.values.receita.count],
    [
      'Assistência 24h',
      'fase-azul',
      money(node.values.despesa_assistencia.amount),
      node.values.despesa_assistencia.count,
    ],
    [
      'Periférico',
      'fase-turquesa',
      money(node.values.despesa_periferico.amount),
      node.values.despesa_periferico.count,
    ],
    [
      'Sinistro',
      'fase-vermelho',
      money(node.values.despesa_sinistro.amount),
      node.values.despesa_sinistro.count,
    ],
  ];
  const s = balanceOf(node);
  return (
    /* Trocar de cartão troca a gaveta num esmaecer curto, e não num corte seco. */
    <Card
      key={node.key}
      className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
      title={node.label}
      descricao={
        parent && parent.kind === 'time' ? `${node.sub} · ${parent.label}` : node.sub
      }
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={s >= 0 ? 'sucesso' : 'erro'}>{formatMoney(s)}</Badge>
          <span className="text-xs text-texto-suave">de saldo no período</span>
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm">
          {rows.map(([label, dot, value, n]) => (
            <div key={label} className="contents">
              <dt className="flex items-center gap-2 text-texto-suave">
                <TeamMapLegendDot dot={dot} />
                {label}
              </dt>
              <dd className="text-right font-semibold tabular-nums">
                {formatMoney(value)}
                <span className="block text-xs font-normal text-texto-tenue">
                  {count(n, 'lançamento', 'lançamentos')}
                </span>
              </dd>
            </div>
          ))}
        </dl>
        {leaves.length > 0 && (
          <div className="grid gap-2">
            <p className="rotulo text-texto-suave">Menor saldo aqui dentro</p>
            <ul className="grid divide-y divide-borda rounded-borda border border-borda">
              {leaves.map((l) => (
                <li key={l.key}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-superficie-suave"
                    onClick={() => onSelect(l.key)}
                  >
                    <span className="min-w-0 truncate">{l.label}</span>
                    <Badge tone={balanceOf(l) >= 0 ? 'sucesso' : 'erro'}>
                      {compact(balanceOf(l))}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
